import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Clock, MapPin, MessageCircle, Tag } from "lucide-react";
import { useSite } from "../lib/SiteContext";
import { useEnquiry } from "../lib/EnquiryContext";
import { waLink } from "../lib/api";
import { optImg } from "../lib/image";
import Seo from "../components/Seo";
import FadeIn from "../components/FadeIn";
import TourCard from "../components/TourCard";

const TourDetail = () => {
  const { slug } = useParams();
  const { tours, content, loading } = useSite();
  const { openEnquiry } = useEnquiry();

  if (loading) return <div className="min-h-screen" data-testid="tour-loading" />;
  const tour = tours.find((t) => t.slug === slug);

  if (!tour) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="tour-not-found">
        <h1 className="font-display text-3xl font-semibold text-pine">Tour not found</h1>
        <p className="mt-3 text-gray-500">This experience may have been updated. Browse all our tours instead.</p>
        <Link to="/tours" className="mt-6 inline-flex items-center gap-2 rounded-full bg-pine px-6 py-3 text-sm font-semibold text-white" data-testid="tour-not-found-back">
          <ArrowLeft className="h-4 w-4" /> Back to all tours
        </Link>
      </div>
    );
  }

  const related = tours.filter((t) => t.id !== tour.id && t.category === tour.category).slice(0, 3);
  const relatedList = related.length ? related : tours.filter((t) => t.id !== tour.id).slice(0, 3);

  return (
    <div data-testid="tour-detail-page">
      <Seo
        title={`${tour.name} — Private Corfu Tour | Aloha Corfu Tours`}
        description={tour.short_description}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TouristTrip",
          "name": tour.name,
          "description": tour.short_description,
          "touristType": "Private tour guests",
          "provider": { "@type": "TravelAgency", "name": "Aloha Corfu Tours", "telephone": "+306983400353" },
        }}
      />

      {/* Hero */}
      <section className="relative h-[46svh] min-h-[340px] w-full overflow-hidden md:h-[60vh]" data-testid="tour-hero">
        <img src={optImg(tour.image_url, 1600, 80)} alt={`${tour.name} — private tour in Corfu`} className="h-full w-full object-cover" fetchpriority="high" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-t from-aegean/85 via-aegean/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
            <Link to="/tours" className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/70 transition-colors hover:text-white" data-testid="back-to-tours-link">
              <ArrowLeft className="h-3.5 w-3.5" /> All Tours
            </Link>
            <h1 className="max-w-3xl text-balance font-display text-3xl font-semibold text-white sm:text-4xl lg:text-5xl" data-testid="tour-title">
              {tour.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-white/85">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 backdrop-blur"><Tag className="h-3.5 w-3.5" />{tour.category}</span>
              {tour.duration && <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 backdrop-blur"><Clock className="h-3.5 w-3.5" />{tour.duration}</span>}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
          {/* Main */}
          <div className="lg:col-span-2">
            <FadeIn>
              <div className="space-y-4 text-base leading-relaxed text-gray-600" data-testid="tour-description">
                {tour.description.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </FadeIn>

            {tour.highlights?.length > 0 && (
              <FadeIn className="mt-10">
                <h2 className="font-display text-2xl font-semibold text-pine">Main highlights</h2>
                <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="tour-highlights">
                  {tour.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5 rounded-xl bg-seafoam-light/60 px-4 py-3 text-sm font-medium text-pine">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-seafoam-dark" /> {h}
                    </li>
                  ))}
                </ul>
              </FadeIn>
            )}

            {tour.itinerary?.length > 0 && (
              <FadeIn className="mt-10">
                <h2 className="font-display text-2xl font-semibold text-pine">Suggested itinerary</h2>
                <p className="mt-1 text-sm text-gray-400">Timings are indicative — every tour is flexible and follows your pace.</p>
                <div className="mt-6" data-testid="tour-itinerary">
                  {tour.itinerary.map((step, i) => (
                    <div key={i} className="relative ml-2 border-l-2 border-seafoam pb-7 pl-6 last:pb-0">
                      <span className="absolute -left-[9px] top-0 h-4 w-4 rounded-full border-2 border-seafoam-dark bg-white" />
                      <div className="flex flex-wrap items-baseline gap-x-3">
                        {step.time && <span className="text-xs font-bold uppercase tracking-wider text-terracotta">{step.time}</span>}
                        <h3 className="font-display text-lg font-semibold text-pine">{step.title}</h3>
                      </div>
                      {step.description && <p className="mt-1 text-sm leading-relaxed text-gray-500">{step.description}</p>}
                    </div>
                  ))}
                </div>
              </FadeIn>
            )}

            {tour.meeting_point && (
              <FadeIn className="mt-10 rounded-2xl border border-gray-100 bg-sand p-5" data-testid="tour-meeting-point">
                <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-pine">
                  <MapPin className="h-5 w-5 text-terracotta" /> Meeting point
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{tour.meeting_point}</p>
              </FadeIn>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <FadeIn delay={0.1}>
              <div className="sticky top-24 rounded-2xl border border-gray-100 bg-white p-6 shadow-lift" data-testid="tour-enquiry-card">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Price on request</p>
                <p className="mt-2 text-sm leading-relaxed text-gray-500">
                  Tell us your date and group size — we reply personally with availability and a no-obligation quote.
                </p>
                {tour.duration && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4 text-seafoam-dark" /> Duration: <span className="font-medium">{tour.duration}</span>
                  </div>
                )}
                <button
                  onClick={() => openEnquiry(tour.name)}
                  data-testid="tour-enquire-button"
                  className="mt-5 w-full rounded-full bg-pine px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-pine-light"
                >
                  Enquire About This Tour
                </button>
                <a
                  href={waLink(content?.settings, `Hello Aloha Corfu Tours! I would like information about the "${tour.name}" tour.`)}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="tour-whatsapp-button"
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.02]"
                >
                  <MessageCircle className="h-4 w-4" /> Ask on WhatsApp
                </a>
                <p className="mt-4 text-center text-xs text-gray-400">No online payment · Fully private · Flexible pace</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="bg-sand py-14 md:py-20" data-testid="related-tours-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-display text-2xl font-semibold text-pine sm:text-3xl">You may also love</h2>
            <Link to="/tours" className="text-sm font-semibold text-pine hover:underline underline-offset-4" data-testid="related-back-link">
              Back to all tours →
            </Link>
          </FadeIn>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relatedList.map((t) => <FadeIn key={t.id}><TourCard tour={t} /></FadeIn>)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TourDetail;
